import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  User, Briefcase, MessageSquare, Settings, Eye, Mail, 
  Plus, Edit, Trash2, Image, Video, MapPin, Banknote,
  BadgeCheck, Star, BarChart3, ExternalLink, Camera, Upload, CreditCard
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../App";
import { formatApiError } from "../lib/utils";
import { FieldError } from "../components/FieldError";
import { CompanyLogoField, uploadImage } from "../components/CompanyLogoField";
import { SubscriptionPanel } from "../components/SubscriptionPanel";
import { SHOW_PAID_PLANS } from "../lib/featureFlags";

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, checkAuth } = useAuth();

  const [profile, setProfile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "profile");

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    business_name: "",
    bio: "",
    tagline: "",
    skills: [],
    services: [],
    location: "",
    experience_level: "",
    availability: "",
    day_rate_min: "",
    day_rate_max: "",
    gear_owned: [],
    website: "",
    whatsapp: "",
    instagram: "",
    vimeo: "",
    youtube: "",
    facebook: "",
    tiktok: "",
    x: "",
    linkedin: "",
  });

  // Portfolio modal state
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [portfolioItem, setPortfolioItem] = useState({
    type: "image",
    url: "",
    title: "",
    description: "",
  });

  // Profile picture upload
  const fileInputRef = useRef(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);

  // Update user from location state if coming from OAuth
  useEffect(() => {
    if (location.state?.user) {
      checkAuth();
    }
  }, [location.state]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [categoriesRes, locationsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/locations`),
      ]);
      setCategories(categoriesRes.data);
      setLocations(locationsRes.data);

      // Fetch user's profile
      try {
        const profileRes = await axios.get(
          `${API}/profiles/user/${user.user_id}`,
          { withCredentials: true }
        );
        setProfile(profileRes.data);
        setProfileForm({
          business_name: profileRes.data.business_name || "",
          bio: profileRes.data.bio || "",
          tagline: profileRes.data.tagline || "",
          skills: profileRes.data.skills || [],
          services: profileRes.data.services || [],
          location: profileRes.data.location || "",
          experience_level: profileRes.data.experience_level || "",
          availability: profileRes.data.availability || "",
          day_rate_min: profileRes.data.day_rate_min || "",
          day_rate_max: profileRes.data.day_rate_max || "",
          gear_owned: profileRes.data.gear_owned || [],
          website: profileRes.data.website || "",
          whatsapp: profileRes.data.whatsapp || "",
          instagram: profileRes.data.instagram || "",
          vimeo: profileRes.data.vimeo || "",
          youtube: profileRes.data.youtube || "",
          facebook: profileRes.data.facebook || "",
          tiktok: profileRes.data.tiktok || "",
          x: profileRes.data.x || "",
          linkedin: profileRes.data.linkedin || "",
        });
      } catch (error) {
        // No profile yet
        setProfile(null);
      }

      // Fetch messages
      try {
        const messagesRes = await axios.get(`${API}/messages`, { withCredentials: true });
        setMessages(messagesRes.data);
      } catch (error) {
        console.error("Error fetching messages:", error);
      }

      // Fetch my applications
      try {
        const applicationsRes = await axios.get(`${API}/my-applications`, { withCredentials: true });
        setMyApplications(applicationsRes.data);
      } catch (error) {
        console.error("Error fetching applications:", error);
      }

    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => {
    const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const min = toNum(profileForm.day_rate_min);
    const max = toNum(profileForm.day_rate_max);
    const errs = {};
    if ((min !== null && (isNaN(min) || min < 0)) || (max !== null && (isNaN(max) || max < 0))) {
      errs.day_rate = "Day rate must be a positive number";
    } else if (min !== null && max !== null && min > max) {
      errs.day_rate = "Minimum rate can't be more than the maximum";
    }
    const urlFields = ["website", "vimeo", "youtube"];
    urlFields.forEach((f) => {
      const v = profileForm[f]?.trim();
      if (v && !/^https?:\/\/\S+\.\S+/.test(v)) errs[f] = "Enter a full link starting with https://";
    });
    setFormErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please fix the highlighted fields");
      return null;
    }
    return { ...profileForm, day_rate_min: min, day_rate_max: max };
  };

  const createProfile = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/profiles`,
        payload,
        { withCredentials: true }
      );
      setProfile(response.data);
      toast.success("Profile created!");
    } catch (error) {
      toast.error(formatApiError(error, "Failed to create profile"));
    } finally {
      setSaving(false);
    }
  };

  const updateProfile = async () => {
    if (!profile) return;
    const payload = buildPayload();
    if (!payload) return;
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/profiles/${profile.profile_id}`,
        payload,
        { withCredentials: true }
      );
      setProfile(response.data);
      toast.success("Profile updated!");
    } catch (error) {
      toast.error(formatApiError(error, "Failed to update profile"));
    } finally {
      setSaving(false);
    }
  };

  const handlePictureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = "";
    setUploadingPicture(true);
    try {
      const url = await uploadImage(file);
      await axios.put(
        `${API}/users/me/picture`,
        { picture: url },
        { withCredentials: true }
      );

      toast.success("Profile picture updated!");
      checkAuth(); // Refresh user data
      fetchData(); // Refresh profile data
    } catch (error) {
      toast.error(error.response ? formatApiError(error, "Failed to upload picture") : error.message);
    } finally {
      setUploadingPicture(false);
    }
  };

  const addPortfolioItem = async () => {
    if (!profile || !portfolioItem.url) {
      toast.error("Please enter a URL");
      return;
    }

    try {
      await axios.post(
        `${API}/profiles/${profile.profile_id}/portfolio`,
        portfolioItem,
        { withCredentials: true }
      );
      toast.success("Portfolio item added!");
      setPortfolioOpen(false);
      setPortfolioItem({ type: "image", url: "", title: "", description: "" });
      fetchData(); // Refresh profile
    } catch (error) {
      toast.error(formatApiError(error, "Failed to add portfolio item"));
    }
  };

  const removePortfolioItem = async (itemId) => {
    if (!profile) return;

    try {
      await axios.delete(
        `${API}/profiles/${profile.profile_id}/portfolio/${itemId}`,
        { withCredentials: true }
      );
      toast.success("Portfolio item removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove portfolio item");
    }
  };

  const toggleSkill = (skill) => {
    setProfileForm((prev) => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter((s) => s !== skill)
        : [...prev.skills, skill],
    }));
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4 md:px-8" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            {/* Profile Picture with Upload */}
            <div className="relative group">
              <div className="w-20 h-20 rounded-full bg-zinc-800 overflow-hidden border-2 border-zinc-700">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" data-testid="profile-photo-preview" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-500 text-3xl font-heading">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}
              </div>
              {/* Upload Overlay */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPicture}
                data-testid="profile-photo-upload-btn"
                aria-label="Upload profile photo"
                className="absolute inset-0 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                {uploadingPicture ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera className="h-6 w-6 text-white" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handlePictureUpload}
                className="hidden"
                data-testid="profile-photo-input"
              />
            </div>
            <div>
              <h1 className="font-heading text-2xl text-white">{user?.name}</h1>
              <p className="text-zinc-500">{user?.email}</p>
              <div className="flex items-center gap-2 mt-1">
                {user?.is_verified && (
                  <span className="badge-verified">
                    <BadgeCheck className="h-3 w-3" />
                    VERIFIED
                  </span>
                )}
                {user?.is_premium && (
                  <span className="badge-premium">
                    <Star className="h-3 w-3" />
                    PRO
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {profile && (
            <Button
              onClick={() => navigate(`/profile/${profile.slug || profile.profile_id}`)}
              data-testid="view-public-profile-btn"
              variant="outline"
              className="border-zinc-700"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Public Profile
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="profile" className="data-[state=active]:bg-orange-600">
              <User className="h-4 w-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="portfolio" className="data-[state=active]:bg-orange-600">
              <Image className="h-4 w-4 mr-2" />
              Portfolio
            </TabsTrigger>
            <TabsTrigger value="jobs" className="data-[state=active]:bg-orange-600">
              <Briefcase className="h-4 w-4 mr-2" />
              Jobs
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-orange-600">
              <MessageSquare className="h-4 w-4 mr-2" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-orange-600">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="billing" className="data-[state=active]:bg-orange-600" data-testid="billing-tab">
              <CreditCard className="h-4 w-4 mr-2" />
              Billing
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Info */}
              <div className="bg-zinc-900 border border-zinc-800 p-6">
                <h3 className="font-heading text-xl text-white mb-6">Basic Information</h3>
                
                <div className="space-y-4">
                  <CompanyLogoField onChanged={fetchData} />
                  <div>
                    <Label className="text-zinc-300">Business / trading name <span className="text-zinc-500 font-normal">(optional)</span></Label>
                    <Input
                      value={profileForm.business_name}
                      onChange={(e) => setProfileForm({ ...profileForm, business_name: e.target.value })}
                      placeholder={`Leave blank to use your name (${user?.name || "your name"})`}
                      maxLength={80}
                      className="mt-2 bg-zinc-800 border-zinc-700"
                      data-testid="profile-business-name-input"
                    />
                    <p className="text-xs text-zinc-500 mt-1" data-testid="profile-url-preview">
                      Your public link: videographycapetown.co.za/profile/{profile?.slug || "your-name"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Tagline</Label>
                    <Input
                      value={profileForm.tagline}
                      onChange={(e) => setProfileForm({ ...profileForm, tagline: e.target.value })}
                      placeholder="e.g., Award-winning cinematographer based in Cape Town"
                      className="mt-2 bg-zinc-800 border-zinc-700"
                    />
                  </div>

                  <div>
                    <Label className="text-zinc-300">Bio</Label>
                    <Textarea
                      value={profileForm.bio}
                      onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                      placeholder="Tell clients about yourself, your experience, and your style..."
                      className="mt-2 min-h-32 bg-zinc-800 border-zinc-700"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-300">Location</Label>
                      <Select
                        value={profileForm.location}
                        onValueChange={(value) => setProfileForm({ ...profileForm, location: value })}
                      >
                        <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700">
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-700">
                          {locations.map((loc) => (
                            <SelectItem key={loc.location_id} value={loc.name}>
                              {loc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-zinc-300">Experience Level</Label>
                      <Select
                        value={profileForm.experience_level}
                        onValueChange={(value) => setProfileForm({ ...profileForm, experience_level: value })}
                      >
                        <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700">
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-700">
                          <SelectItem value="entry">Entry Level</SelectItem>
                          <SelectItem value="mid">Mid Level</SelectItem>
                          <SelectItem value="senior">Senior</SelectItem>
                          <SelectItem value="expert">Expert</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-300">Availability</Label>
                      <Select
                        value={profileForm.availability}
                        onValueChange={(value) => setProfileForm({ ...profileForm, availability: value })}
                      >
                        <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700">
                          <SelectValue placeholder="Select availability" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-700">
                          <SelectItem value="available">Available Now</SelectItem>
                          <SelectItem value="busy">Busy</SelectItem>
                          <SelectItem value="unavailable">Unavailable</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-zinc-300">Day Rate (ZAR)</Label>
                      <div className="flex items-center gap-2 mt-2">
                        <Input
                          type="number"
                          value={profileForm.day_rate_min}
                          onChange={(e) => setProfileForm({ ...profileForm, day_rate_min: e.target.value })}
                          placeholder="Min"
                          className="bg-zinc-800 border-zinc-700"
                          data-testid="day-rate-min-input"
                        />
                        <span className="text-zinc-600">-</span>
                        <Input
                          type="number"
                          value={profileForm.day_rate_max}
                          onChange={(e) => setProfileForm({ ...profileForm, day_rate_max: e.target.value })}
                          placeholder="Max"
                          className="bg-zinc-800 border-zinc-700"
                          data-testid="day-rate-max-input"
                        />
                      </div>
                      <FieldError message={formErrors.day_rate} testId="day-rate-error" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Skills & Services */}
              <div className="bg-zinc-900 border border-zinc-800 p-6">
                <h3 className="font-heading text-xl text-white mb-6">Skills & Services</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-zinc-300">Skills (Select from categories)</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {categories.map((cat) => (
                        <button
                          key={cat.category_id}
                          onClick={() => toggleSkill(cat.name)}
                          className={`px-3 py-1 text-sm border transition-colors ${
                            profileForm.skills.includes(cat.name)
                              ? "bg-orange-600 border-orange-600 text-white"
                              : "bg-transparent border-zinc-700 text-zinc-400 hover:border-zinc-500"
                          }`}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact & Social */}
              <div className="bg-zinc-900 border border-zinc-800 p-6 lg:col-span-2">
                <h3 className="font-heading text-xl text-white mb-6">Contact & Social</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-zinc-300">WhatsApp</Label>
                    <Input
                      value={profileForm.whatsapp}
                      onChange={(e) => setProfileForm({ ...profileForm, whatsapp: e.target.value })}
                      placeholder="+27 82 123 4567"
                      className="mt-2 bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Website</Label>
                    <Input
                      value={profileForm.website}
                      onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                      placeholder="https://yourwebsite.com"
                      className="mt-2 bg-zinc-800 border-zinc-700"
                      data-testid="profile-website-input"
                    />
                    <FieldError message={formErrors.website} testId="profile-website-error" />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Instagram</Label>
                    <Input
                      value={profileForm.instagram}
                      onChange={(e) => setProfileForm({ ...profileForm, instagram: e.target.value })}
                      placeholder="@yourusername or full link"
                      className="mt-2 bg-zinc-800 border-zinc-700"
                      data-testid="profile-instagram-input"
                    />
                  </div>
                  {[
                    { field: "facebook", label: "Facebook", placeholder: "Page name or full link" },
                    { field: "tiktok", label: "TikTok", placeholder: "@yourusername or full link" },
                    { field: "x", label: "X (Twitter)", placeholder: "@yourusername or full link" },
                    { field: "linkedin", label: "LinkedIn", placeholder: "Full profile or company link" },
                  ].map(({ field, label, placeholder }) => (
                    <div key={field}>
                      <Label className="text-zinc-300">{label}</Label>
                      <Input
                        value={profileForm[field]}
                        onChange={(e) => setProfileForm({ ...profileForm, [field]: e.target.value })}
                        placeholder={placeholder}
                        className="mt-2 bg-zinc-800 border-zinc-700"
                        data-testid={`profile-${field}-input`}
                      />
                    </div>
                  ))}
                  <div>
                    <Label className="text-zinc-300">Vimeo</Label>
                    <Input
                      value={profileForm.vimeo}
                      onChange={(e) => setProfileForm({ ...profileForm, vimeo: e.target.value })}
                      placeholder="https://vimeo.com/yourprofile"
                      className="mt-2 bg-zinc-800 border-zinc-700"
                      data-testid="profile-vimeo-input"
                    />
                    <FieldError message={formErrors.vimeo} testId="profile-vimeo-error" />
                  </div>
                  <div>
                    <Label className="text-zinc-300">YouTube</Label>
                    <Input
                      value={profileForm.youtube}
                      onChange={(e) => setProfileForm({ ...profileForm, youtube: e.target.value })}
                      placeholder="https://youtube.com/c/yourchannel"
                      className="mt-2 bg-zinc-800 border-zinc-700"
                      data-testid="profile-youtube-input"
                    />
                    <FieldError message={formErrors.youtube} testId="profile-youtube-error" />
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={profile ? updateProfile : createProfile}
                disabled={saving}
                className="bg-orange-600 hover:bg-orange-500"
                data-testid="save-profile-btn"
              >
                {saving ? "Saving..." : profile ? "Update Profile" : "Create Profile"}
              </Button>
            </div>
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio" className="space-y-6">
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-xl text-white">Portfolio Items</h3>
                <Dialog open={portfolioOpen} onOpenChange={setPortfolioOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-orange-600 hover:bg-orange-500" disabled={!profile}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Item
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                      <DialogTitle>Add Portfolio Item</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label className="text-zinc-300">Type</Label>
                        <Select
                          value={portfolioItem.type}
                          onValueChange={(value) => setPortfolioItem({ ...portfolioItem, type: value })}
                        >
                          <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-700">
                            <SelectItem value="image">Image</SelectItem>
                            <SelectItem value="video">Video (YouTube/Vimeo)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-zinc-300">URL</Label>
                        <Input
                          value={portfolioItem.url}
                          onChange={(e) => setPortfolioItem({ ...portfolioItem, url: e.target.value })}
                          placeholder={portfolioItem.type === "video" ? "https://vimeo.com/..." : "https://..."}
                          className="mt-2 bg-zinc-800 border-zinc-700"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300">Title (optional)</Label>
                        <Input
                          value={portfolioItem.title}
                          onChange={(e) => setPortfolioItem({ ...portfolioItem, title: e.target.value })}
                          placeholder="Project name"
                          className="mt-2 bg-zinc-800 border-zinc-700"
                        />
                      </div>
                      <Button onClick={addPortfolioItem} className="w-full bg-orange-600 hover:bg-orange-500">
                        Add to Portfolio
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {!profile ? (
                <p className="text-zinc-500 text-center py-8">
                  Create your profile first to add portfolio items
                </p>
              ) : profile.portfolio?.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {profile.portfolio.map((item) => (
                    <div
                      key={item.item_id}
                      className="relative group aspect-video bg-zinc-800 overflow-hidden"
                    >
                      {item.type === "image" ? (
                        <img
                          src={item.url}
                          alt={item.title || "Portfolio"}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="h-8 w-8 text-zinc-500" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removePortfolioItem(item.item_id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-center py-8">
                  No portfolio items yet. Add your best work!
                </p>
              )}
            </div>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="space-y-6">
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <h3 className="font-heading text-xl text-white mb-6">My Applications</h3>
              {myApplications.length > 0 ? (
                <div className="space-y-4">
                  {myApplications.map((app) => (
                    <div key={app.application_id} className="p-4 border border-zinc-800 hover:border-zinc-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">Job ID: {app.job_id}</p>
                          <p className="text-sm text-zinc-500">{new Date(app.created_at).toLocaleDateString()}</p>
                        </div>
                        <Badge
                          className={
                            app.status === "accepted"
                              ? "bg-teal-900/50 text-teal-400"
                              : app.status === "rejected"
                              ? "bg-red-900/50 text-red-400"
                              : "bg-zinc-800 text-zinc-400"
                          }
                        >
                          {app.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-center py-8">
                  You haven't applied to any jobs yet
                </p>
              )}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-6">
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <h3 className="font-heading text-xl text-white mb-6">Messages</h3>
              {messages.length > 0 ? (
                <div className="space-y-4">
                  {messages.slice(0, 20).map((msg) => (
                    <div key={msg.message_id} className="p-4 border border-zinc-800">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-zinc-800 overflow-hidden shrink-0">
                          {msg.sender_picture ? (
                            <img src={msg.sender_picture} alt={msg.sender_name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-zinc-500">
                              {msg.sender_name?.charAt(0)?.toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-white">{msg.sender_name}</p>
                            <span className="text-xs text-zinc-500">
                              {new Date(msg.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-zinc-400 mt-1">{msg.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-center py-8">No messages yet</p>
              )}
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
                <Eye className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                <p className="font-heading text-3xl text-white">{profile?.view_count || 0}</p>
                <p className="text-zinc-500">Profile Views</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
                <Mail className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                <p className="font-heading text-3xl text-white">{profile?.contact_count || 0}</p>
                <p className="text-zinc-500">Contact Clicks</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
                <MessageSquare className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                <p className="font-heading text-3xl text-white">{messages.length}</p>
                <p className="text-zinc-500">Messages Received</p>
              </div>
            </div>

            {!user?.is_premium && SHOW_PAID_PLANS && (
              <div className="bg-gradient-to-r from-orange-900/30 to-red-900/30 border border-orange-800 p-6 text-center">
                <h3 className="font-heading text-xl text-white mb-2">Upgrade to Pro</h3>
                <p className="text-zinc-400 mb-4">
                  Get detailed analytics, priority placement, and more!
                </p>
                <Button
                  onClick={() => navigate("/pricing")}
                  className="bg-orange-600 hover:bg-orange-500"
                >
                  View Plans
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="billing" className="space-y-6">
            <SubscriptionPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
